if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      getAddonSelections(formData) {
        const productInfo = this.closest('product-info');
        if (!productInfo) return [];

        const addonRoot = productInfo.querySelector('[data-sg-addons]');
        if (!addonRoot) return [];

        const certSelect = addonRoot.querySelector('#sg-cert');
        const pujaSelect = addonRoot.querySelector('#sg-puja');

        const certValue = certSelect?.value || '';
        const pujaValue = pujaSelect?.value || '';

        if (certValue) formData.set('properties[Lab Certificate]', certValue);
        if (pujaValue) formData.set('properties[puja-option]', pujaValue);

        const selectedAddons = [];
        [certSelect, pujaSelect].forEach((selectEl) => {
          if (!selectEl) return;
          const selectedOption = selectEl.options[selectEl.selectedIndex];
          const variantId = selectedOption?.dataset?.variantId;
          if (!variantId) return;

          selectedAddons.push({
            id: Number(variantId),
            title: selectedOption.textContent.trim(),
            addonType: selectedOption?.dataset?.addonType || '',
          });
        });

        return selectedAddons;
      }

      addAddonItems({ addons, quantity, parentToken }) {
        if (!addons.length) return Promise.resolve();

        const items = addons.map((addon) => ({
          id: addon.id,
          quantity,
          properties: {
            _sg_is_addon: '1',
            _sg_parent_token: parentToken,
            _sg_addon_title: addon.title,
            _sg_addon_type: addon.addonType,
          },
        }));

        return fetch(`${routes.cart_add_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items }),
        })
          .then((response) => {
            if (!response.ok) throw new Error('Unable to add add-on items.');
            return response.json();
          })
          .then(() => fetch(`${routes.cart_url}.js`))
          .then((response) => {
            if (!response.ok) throw new Error('Unable to fetch updated cart.');
            return response.json();
          })
          .then((updatedCart) =>
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'sg-addons',
              cartData: updatedCart,
            })
          );
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const selectedAddons = this.getAddonSelections(formData);
        const mainQuantity = Number(formData.get('quantity') || 1);
        if (selectedAddons.length) {
          formData.set('properties[_sg_parent_token]', `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
        }
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              const parentToken = formData.get('properties[_sg_parent_token]');
              this.addAddonItems({
                addons: selectedAddons,
                quantity: mainQuantity,
                parentToken,
              }).finally(() => {
                window.location = window.routes.cart_url;
              });
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            const parentToken = formData.get('properties[_sg_parent_token]');
            this.addAddonItems({
              addons: selectedAddons,
              quantity: mainQuantity,
              parentToken,
            }).catch((error) => console.error(error));

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }

          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
